import React, { useState } from "react";
import { RotateCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";

/**
 * ForceRefreshButton
 * ------------------
 * Manual "nuke the cache" escape hatch for users on mobile PWAs (especially
 * Samsung/Chrome) who occasionally see stale data. This is a deliberate,
 * always-visible fallback in addition to AppUpdateBanner's automatic SW
 * skip-waiting logic — it gives users a tactile "I'm stuck, fetch me the
 * latest version NOW" affordance.
 *
 * Tap flow:
 *   1. Confirm dialog (so accidental taps don't blow away an active workout).
 *   2. Unregister every Service Worker registration.
 *   3. Delete every Cache Storage bucket.
 *   4. Hard-reload the page.
 *
 * Placement: floating, fixed top-right, sits above all page content but below
 * the AppUpdateBanner (which uses z-60).
 */
export default function ForceRefreshButton() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleRefresh = async () => {
    setBusy(true);
    try {
      // 1. Unregister all service workers.
      if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
        try {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister().catch(() => null)));
        } catch { /* ignore */ }
      }

      // 2. Delete every Cache Storage bucket the app/SW created.
      if (typeof caches !== "undefined") {
        try {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k).catch(() => null)));
        } catch { /* ignore */ }
      }

      toast.success("Cache cleared — reloading...");
      // 3. Hard reload. The query param busts any HTTP cache on index.html.
      setTimeout(() => {
        const u = new URL(window.location.href);
        u.searchParams.set("_r", String(Date.now()));
        window.location.replace(u.toString());
      }, 400);
    } catch (e) {
      toast.error("Refresh failed — try closing & reopening the app");
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
      <AlertDialogTrigger asChild>
        <button
          data-testid="force-refresh-btn"
          aria-label="Force refresh & clear cache"
          title="Force refresh & clear cache"
          className="fixed z-50 top-3 right-3 w-9 h-9 flex items-center justify-center bg-neutral-900/80 hover:bg-neutral-800 active:bg-neutral-700 border border-white/10 backdrop-blur text-white/70 hover:text-white shadow-lg transition-colors"
          style={{ marginTop: "env(safe-area-inset-top)" }}
        >
          <RotateCw className="w-4 h-4" />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="force-refresh-dialog" className="bg-neutral-950 border-white/10 text-white">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display uppercase tracking-wider text-xl">
            Force refresh?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-white/70">
            This clears the app's cache and reloads to pull the latest version.
            Use this if you're seeing stale data. Any active workout in progress
            will be saved server-side, but unsaved local edits will be lost.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid="force-refresh-cancel"
            disabled={busy}
            className="bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="force-refresh-confirm"
            disabled={busy}
            onClick={(e) => {
              e.preventDefault();
              handleRefresh();
            }}
            className="bg-blue-500 hover:bg-blue-600 text-white font-display uppercase tracking-wider"
          >
            {busy ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Clearing…
              </span>
            ) : (
              "Clear & Reload"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
